import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { ApiError, errorMessage } from '@/lib/api';
import { isMiniApp } from '@/lib/telegram';
import { useAuthStore } from '@/store/auth';

/**
 * Вход в панель.
 *
 * Разметка перенесена из старой панели дословно: `.login-wrap`, `.login-card`,
 * `.brand`, `.field`, `.btn-full`, `.error-text` — всё это уже есть в
 * index.css. Изменились ровно две вещи:
 *   — вместо логина email (учётки платформы заводятся по почте);
 *   — в Mini App форма может не понадобиться вовсе: сессия лежит в
 *     CloudStorage, и bootstrap поднимет её сам.
 */

/** Куда возвращаться после входа. */
interface FromState {
  from?: string;
}

export default function Login(): ReactNode {
  const navigate = useNavigate();
  const location = useLocation();
  const status = useAuthStore((state) => state.status);
  const login = useAuthStore((state) => state.login);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const emailRef = useRef<HTMLInputElement>(null);

  // Возврат на исходный путь: оператор открыл ссылку на диалог, его увели на
  // вход — после входа он обязан попасть именно туда, а не в общий список.
  const from = (location.state as FromState | null)?.from ?? '/inbox';

  useEffect(() => {
    // В Mini App поле ввода не фокусируем: фокус поднимает клавиатуру поверх
    // формы ещё до того, как оператор понял, нужен ли ему вообще ручной вход.
    if (!isMiniApp()) emailRef.current?.focus();
  }, []);

  if (status === 'authenticated') return <Navigate to={from} replace />;

  /*
   * Пока идёт восстановление сессии, форму не показываем.
   *
   * В Mini App это почти всегда заканчивается автовходом по refresh-токену из
   * CloudStorage, и мелькнувшая на долю секунды форма выглядит как разлогин.
   */
  if (status === 'loading') {
    return (
      <div className="login-wrap">
        <div className="login-card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          Входим…
        </div>
      </div>
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await login(email.trim(), password);
      void navigate(from, { replace: true });
    } catch (caught) {
      setError(describe(caught));
      setPassword('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login-card" onSubmit={(event) => void submit(event)}>
        <div className="brand">
          <div className="brand-mark">SP</div>
          <div>
            <h1>Поддержка</h1>
            <p className="sub">Панель операторов</p>
          </div>
        </div>

        <div className="field">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            ref={emailRef}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="username"
            spellCheck={false}
            disabled={busy}
          />
        </div>

        <div className="field">
          <label htmlFor="login-password">Пароль</label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            disabled={busy}
          />
        </div>

        {error && (
          <p className="error-text" role="alert">
            {error}
          </p>
        )}

        <button
          className="btn btn-full"
          type="submit"
          disabled={busy || !email.trim() || !password}
          style={{ marginTop: 10 }}
        >
          {busy ? 'Вход…' : 'Войти'}
        </button>
      </form>
    </div>
  );
}

/**
 * Текст отказа для оператора.
 *
 * Сервер намеренно отвечает одинаково на «нет такого пользователя» и «неверный
 * пароль» — иначе форма превращается в перечислитель существующих адресов.
 * Разделяем только то, что оператор может исправить сам: сеть, лимит попыток,
 * отключённую учётку.
 */
function describe(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isNetwork) return 'Нет связи с сервером. Проверьте подключение.';
    if (error.status === 429) {
      return 'Слишком много попыток входа. Подождите несколько минут.';
    }
    if (error.status === 401) return error.message || 'Неверный email или пароль';
    if (error.status === 403) return error.message || 'Учётная запись отключена';
  }
  return errorMessage(error, 'Не удалось войти');
}
